import type { SchedulingWindowReference } from './EditSchedulingWindowReference';

// The wire format for a scheduling-window cross-reference is
// `zone_name|window_name`; the backend validates it with `/^[^|]+\|[^|]+$/` and
// resolves it to an id on write. Editable entities hold the object form so a
// reference picker can bind to it directly, and stringify only at the `toSave`
// boundary — so the parse lives here rather than in each entity.

/**
 * Parse a wire path into the object form, or `null` when there is nothing to
 * pick.
 *
 * Anything that is not two non-empty halves yields `null`, matching the shape the
 * backend itself accepts (`/^[^|]+\|[^|]+$/`). That also covers its fallback of
 * rendering the bare id as text when a join finds no window: such a value cannot
 * be shown in a reference picker, so the field reads as empty and a subsequent
 * save clears it. Effectively unreachable while the foreign keys hold — the join
 * only fails if the id does not resolve.
 */
export const parseSchedulingWindowPath = (path: string | null | undefined): SchedulingWindowReference | null => {
  if (path === null || path === undefined) return null;
  const [zone_name, name] = path.split('|');
  return zone_name !== undefined && zone_name !== '' && name !== undefined && name !== '' ? { zone_name, name } : null;
};

/** Project back to the wire path, or `null` when nothing is picked. */
export const formatSchedulingWindowPath = (window: SchedulingWindowReference | null): string | null =>
  window !== null ? `${window.zone_name}|${window.name}` : null;
